import { PaletteMode, Components, Theme } from '@mui/material';
import { CSSProperties } from 'react';
import { themePalette } from '../theme.palette';
import { typographyTokens } from '../typography-tokens';
import { palette } from '../palette';
import dimensions from '../dimensions';

const { neutrals } = palette;

// Define custom variant interfaces
declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    primary: true;
    secondary: true;
    outline: true;
    ghost: true;
    destructive: true;
    'link-primary': true;
    'link-secondary': true;
    'link-destructive': true;
    'link-orange': true;
    abstract: true;
  }

  interface ButtonPropsSizeOverrides {
    xlarge: true;
  }
}

export const MuiButtonTheme = (mode: PaletteMode): Components<Theme>['MuiButton'] => {
  const { componentTokens, tokens } = themePalette(mode);
  const { spacing } = dimensions;

  const commonStyles: CSSProperties = {
    outline: 'none',
    transition:
      'border-color 100ms cubic-bezier(0.0, 0, 0.2, 1) 0ms, background-color 100ms cubic-bezier(0.0, 0, 0.2, 1) 0ms',
    border: '1px solid',
    // Typography properties
    letterSpacing: 'normal',
    textTransform: 'none',
    fontWeight: typographyTokens.fontWeights.medium,
    fontFamily: typographyTokens.fontFamilies.sans,
    // Layout properties - using theme spacing tokens
    borderRadius: `${spacing(1.5)}px`, // 6px
    paddingLeft: spacing(4), // 16px
    paddingRight: spacing(4), // 16px
    paddingTop: 7,
    paddingBottom: 7,
  };

  return {
    defaultProps: {
      disableRipple: true,
    },
    variants: [
      // Primary variant
      {
        props: { variant: 'primary' },
        style: {
          ...commonStyles,
          color: componentTokens.buttons.colors.coreIcon.primary.text.resting,
          backgroundColor: componentTokens.buttons.colors.coreIcon.primary.background.resting,
          borderColor: componentTokens.buttons.colors.coreIcon.primary.stroke.resting,
          '&:hover': {
            color: componentTokens.buttons.colors.coreIcon.primary.text.hover,
            backgroundColor: componentTokens.buttons.colors.coreIcon.primary.background.hover,
            borderColor: componentTokens.buttons.colors.coreIcon.primary.stroke.hover,
          },
          '&:focus-visible': {
            color: componentTokens.buttons.colors.coreIcon.primary.text.selected,
            backgroundColor: componentTokens.buttons.colors.coreIcon.primary.background.selected,
            borderColor: componentTokens.buttons.colors.coreIcon.primary.stroke.selected,
          },
          '&:active': {
            backgroundColor: componentTokens.buttons.colors.coreIcon.primary.background.selected,
            borderColor: componentTokens.buttons.colors.coreIcon.primary.stroke.selected,
          },
          '&:disabled': {
            color: componentTokens.buttons.colors.coreIcon.primary.text.resting,
          },
        },
      },
      // Secondary variant
      {
        props: { variant: 'secondary' },
        style: {
          ...commonStyles,
          color: componentTokens.buttons.colors.coreIcon.secondary.text.resting,
          backgroundColor: componentTokens.buttons.colors.coreIcon.secondary.background.resting,
          borderColor: componentTokens.buttons.colors.coreIcon.secondary.stroke.resting,
          '&:hover': {
            color: componentTokens.buttons.colors.coreIcon.secondary.text.hover,
            backgroundColor: componentTokens.buttons.colors.coreIcon.secondary.background.hover,
            borderColor: componentTokens.buttons.colors.coreIcon.secondary.stroke.hover,
          },
          '&:focus-visible': {
            color: componentTokens.buttons.colors.coreIcon.secondary.text.hover,
            backgroundColor: componentTokens.buttons.colors.coreIcon.secondary.background.hover,
            borderColor: componentTokens.buttons.colors.coreIcon.secondary.stroke.hover,
          },
          '&:active': {
            color: componentTokens.buttons.colors.coreIcon.secondary.text.selected,
            backgroundColor: componentTokens.buttons.colors.coreIcon.secondary.background.selected,
            borderColor: componentTokens.buttons.colors.coreIcon.secondary.stroke.selected,
          },
          '&:disabled': {
            color: componentTokens.buttons.colors.coreIcon.secondary.text.resting,
          },
        },
      },
      {
        props: { variant: 'link-primary' },
        style: {
          ...commonStyles,
          minWidth: 0,
          color: componentTokens.buttons.colors.link.primary.text.resting,
          backgroundColor: componentTokens.buttons.colors.link.primary.underlineDetail.resting,
          borderColor: componentTokens.buttons.colors.link.primary.underlineDetail.resting,
          '&:hover': {
            color: componentTokens.buttons.colors.link.primary.text.hover,
            backgroundColor: componentTokens.buttons.colors.link.primary.underlineDetail.hover,
            borderColor: componentTokens.buttons.colors.link.primary.underlineDetail.hover,
          },
          '&:focus-visible': {
            color: componentTokens.buttons.colors.link.primary.text.hover,
            backgroundColor: componentTokens.buttons.colors.link.primary.underlineDetail.hover,
            borderColor: componentTokens.buttons.colors.link.primary.underlineDetail.hover,
          },
          '&:active': {
            color: componentTokens.buttons.colors.link.primary.text.selected,
            backgroundColor: componentTokens.buttons.colors.link.primary.underlineDetail.selected,
            borderColor: componentTokens.buttons.colors.link.primary.underlineDetail.selected,
          },
          '&:disabled': {
            color: componentTokens.buttons.colors.link.primary.text.resting,
          },
        },
      },
      {
        props: { variant: 'link-secondary' },
        style: {
          ...commonStyles,
          minWidth: 0,
          color: componentTokens.buttons.colors.link.secondary.text.resting,
          backgroundColor: componentTokens.buttons.colors.link.secondary.underlineDetail.resting,
          borderColor: componentTokens.buttons.colors.link.secondary.underlineDetail.resting,
          '&:hover': {
            color: componentTokens.buttons.colors.link.secondary.text.hover,
            backgroundColor: componentTokens.buttons.colors.link.secondary.underlineDetail.hover,
            borderColor: componentTokens.buttons.colors.link.secondary.underlineDetail.hover,
          },
          '&:focus-visible': {
            color: componentTokens.buttons.colors.link.secondary.text.hover,
            backgroundColor: componentTokens.buttons.colors.link.secondary.underlineDetail.hover,
            borderColor: componentTokens.buttons.colors.link.secondary.underlineDetail.hover,
          },
          '&:active': {
            color: componentTokens.buttons.colors.link.secondary.text.selected,
            backgroundColor: componentTokens.buttons.colors.link.secondary.underlineDetail.selected,
            borderColor: componentTokens.buttons.colors.link.secondary.underlineDetail.selected,
          },
          '&:disabled': {
            color: componentTokens.buttons.colors.link.secondary.text.resting,
          },
        },
      },
      {
        props: { variant: 'link-orange' },
        style: {
          ...commonStyles,
          minWidth: 0,
          color: componentTokens.buttons.colors.link.orange.text.resting,
          backgroundColor: componentTokens.buttons.colors.link.orange.underlineDetail.resting,
          borderColor: componentTokens.buttons.colors.link.orange.underlineDetail.resting,
          '&:hover': {
            color: componentTokens.buttons.colors.link.orange.text.hover,
            backgroundColor: componentTokens.buttons.colors.link.orange.underlineDetail.hover,
            borderColor: componentTokens.buttons.colors.link.orange.underlineDetail.hover,
          },
          '&:focus-visible': {
            color: componentTokens.buttons.colors.link.orange.text.hover,
            backgroundColor: componentTokens.buttons.colors.link.orange.underlineDetail.hover,
            borderColor: componentTokens.buttons.colors.link.orange.underlineDetail.hover,
          },
          '&:active': {
            color: componentTokens.buttons.colors.link.orange.text.selected,
            backgroundColor: componentTokens.buttons.colors.link.orange.underlineDetail.selected,
            borderColor: componentTokens.buttons.colors.link.orange.underlineDetail.selected,
          },
          '&:disabled': {
            color: componentTokens.buttons.colors.link.destructive.text.resting,
          },
        },
      },
      {
        props: { variant: 'link-destructive' },
        style: {
          ...commonStyles,
          minWidth: 0,
          color: componentTokens.buttons.colors.link.destructive.text.resting,
          backgroundColor: componentTokens.buttons.colors.link.destructive.underlineDetail.resting,
          borderColor: componentTokens.buttons.colors.link.destructive.underlineDetail.resting,
          '&:hover': {
            color: componentTokens.buttons.colors.link.destructive.text.hover,
            backgroundColor: componentTokens.buttons.colors.link.destructive.underlineDetail.hover,
            borderColor: componentTokens.buttons.colors.link.destructive.underlineDetail.hover,
          },
          '&:focus-visible': {
            color: componentTokens.buttons.colors.link.destructive.text.hover,
            backgroundColor: componentTokens.buttons.colors.link.destructive.underlineDetail.hover,
            borderColor: componentTokens.buttons.colors.link.destructive.underlineDetail.hover,
          },
          '&:active': {
            color: componentTokens.buttons.colors.link.destructive.text.selected,
            backgroundColor:
              componentTokens.buttons.colors.link.destructive.underlineDetail.selected,
            borderColor: componentTokens.buttons.colors.link.destructive.underlineDetail.selected,
          },
          '&:disabled': {
            color: componentTokens.buttons.colors.link.destructive.text.resting,
          },
        },
      },
      // Abstract variant
      {
        props: { variant: 'abstract' },
        style: {
          type: 'button',
          cursor: 'pointer',
          color: tokens.content.text.neutral.base,
          textDecoration: 'none',
          backgroundColor: neutrals.clear,
          transition: 'all 0.1s linear',
          margin: 0,
          padding: 0,
          border: 'none',
          outline: 'none',
          fontWeight: 'normal',
          fontSize: typographyTokens.fontSizes.lg,
          height: 'auto',
          lineHeight: 'normal',
        },
      },
      // Outline variant
      {
        props: { variant: 'outline' },
        style: {
          ...commonStyles,
          color: componentTokens.buttons.colors.coreIcon.outline.text.resting,
          backgroundColor: componentTokens.buttons.colors.coreIcon.outline.background.resting,
          borderColor: componentTokens.buttons.colors.coreIcon.outline.stroke.resting,
          '& .MuiSvgIcon-root': {
            color: componentTokens.buttons.colors.coreIcon.outline.assets.resting,
          },
          '&:hover': {
            color: componentTokens.buttons.colors.coreIcon.outline.text.hover,
            backgroundColor: componentTokens.buttons.colors.coreIcon.outline.background.hover,
            borderColor: componentTokens.buttons.colors.coreIcon.outline.stroke.hover,
            '& .MuiSvgIcon-root': {
              color: componentTokens.buttons.colors.coreIcon.outline.assets.hover,
            },
          },
          '&:focus-visible': {
            color: componentTokens.buttons.colors.coreIcon.outline.text.hover,
            backgroundColor: componentTokens.buttons.colors.coreIcon.outline.background.hover,
            borderColor: componentTokens.buttons.colors.coreIcon.outline.stroke.hover,
            '& .MuiSvgIcon-root': {
              color: componentTokens.buttons.colors.coreIcon.outline.assets.hover,
            },
          },
          '&:active': {
            color: componentTokens.buttons.colors.coreIcon.outline.text.selected,
            backgroundColor: componentTokens.buttons.colors.coreIcon.outline.background.selected,
            borderColor: componentTokens.buttons.colors.coreIcon.outline.stroke.selected,
            '& .MuiSvgIcon-root': {
              color: componentTokens.buttons.colors.coreIcon.outline.assets.selected,
            },
          },
          '&:disabled': {
            color: componentTokens.buttons.colors.coreIcon.outline.text.resting,
          },
        },
      },
      // Ghost variant
      {
        props: { variant: 'ghost' },
        style: {
          ...commonStyles,
          color: componentTokens.buttons.colors.coreIcon.ghost.text.resting,
          backgroundColor: componentTokens.buttons.colors.coreIcon.ghost.background.resting,
          borderColor: componentTokens.buttons.colors.coreIcon.ghost.stroke.resting,
          '& .MuiSvgIcon-root': {
            color: componentTokens.buttons.colors.coreIcon.ghost.assets.resting,
          },
          '&:hover': {
            color: componentTokens.buttons.colors.coreIcon.ghost.text.hover,
            backgroundColor: componentTokens.buttons.colors.coreIcon.ghost.background.hover,
            borderColor: componentTokens.buttons.colors.coreIcon.ghost.stroke.hover,
            '& .MuiSvgIcon-root': {
              color: componentTokens.buttons.colors.coreIcon.ghost.assets.hover,
            },
          },
          '&:focus-visible': {
            color: componentTokens.buttons.colors.coreIcon.ghost.text.hover,
            backgroundColor: componentTokens.buttons.colors.coreIcon.ghost.background.hover,
            borderColor: componentTokens.buttons.colors.coreIcon.ghost.stroke.hover,
            '& .MuiSvgIcon-root': {
              color: componentTokens.buttons.colors.coreIcon.ghost.assets.hover,
            },
          },
          '&:active': {
            color: componentTokens.buttons.colors.coreIcon.ghost.text.selected,
            backgroundColor: componentTokens.buttons.colors.coreIcon.ghost.background.selected,
            borderColor: componentTokens.buttons.colors.coreIcon.ghost.stroke.selected,
            '& .MuiSvgIcon-root': {
              color: componentTokens.buttons.colors.coreIcon.ghost.assets.selected,
            },
          },
          '&:disabled': {
            color: componentTokens.buttons.colors.coreIcon.ghost.text.resting,
            '& .MuiSvgIcon-root': {
              color: componentTokens.buttons.colors.coreIcon.ghost.assets.resting,
            },
          },
        },
      },
      // Destructive variant
      {
        props: { variant: 'destructive' },
        style: {
          ...commonStyles,
          color: componentTokens.buttons.colors.coreIcon.destructive.text.resting,
          backgroundColor: componentTokens.buttons.colors.coreIcon.destructive.background.resting,
          borderColor: componentTokens.buttons.colors.coreIcon.destructive.stroke.resting,
          '&:hover': {
            color: componentTokens.buttons.colors.coreIcon.destructive.text.hover,
            backgroundColor: componentTokens.buttons.colors.coreIcon.destructive.background.hover,
            borderColor: componentTokens.buttons.colors.coreIcon.destructive.stroke.hover,
          },
          '&:focus-visible': {
            color: componentTokens.buttons.colors.coreIcon.destructive.text.hover,
            backgroundColor: componentTokens.buttons.colors.coreIcon.destructive.background.hover,
            borderColor: componentTokens.buttons.colors.coreIcon.destructive.stroke.hover,
          },
          '&:active': {
            color: componentTokens.buttons.colors.coreIcon.destructive.text.selected,
            backgroundColor:
              componentTokens.buttons.colors.coreIcon.destructive.background.selected,
            borderColor: componentTokens.buttons.colors.coreIcon.destructive.stroke.selected,
          },
          '&:disabled': {
            color: componentTokens.buttons.colors.coreIcon.destructive.text.resting,
          },
        },
      },
    ],
    styleOverrides: {
      root: {
        ...commonStyles,
        // Override MUI's default disabled opacity with higher specificity
        '&.Mui-disabled': {
          color: 'inherit',
          opacity: 0.4,
        },
        '& .MuiSvgIcon-root': {
          width: '18px',
          height: '18px',
        },
        '&.MuiButton-sizeSmall': {
          paddingTop: spacing(1.5),
          paddingBottom: spacing(1.5),
          fontSize: typographyTokens.fontSizes.xs,
          lineHeight: typographyTokens.lineHeights.xs,
        },
        '&.MuiButton-sizeMedium': {
          paddingTop: spacing(1.5),
          paddingBottom: spacing(1.5),
          fontSize: typographyTokens.fontSizes.sm,
          lineHeight: typographyTokens.lineHeights.sm,
        },
        '&.MuiButton-sizeXlarge': {
          minHeight: '48px',
          fontSize: typographyTokens.fontSizes.base,
          lineHeight: typographyTokens.lineHeights.base,
          paddingTop: 11,
          paddingBottom: 11,
        },
      },
      // Size variants
      sizeSmall: {
        fontSize: typographyTokens.fontSizes.xs,
        lineHeight: typographyTokens.lineHeights.xs,
      },
      sizeMedium: {
        fontSize: typographyTokens.fontSizes.sm,
        lineHeight: typographyTokens.lineHeights.sm,
      },
      sizeLarge: {
        fontSize: typographyTokens.fontSizes.base,
        lineHeight: typographyTokens.lineHeights.base,
      },
    },
  };
};
